import { useState } from 'react';
import type { Poster } from '../api/types';
import Modal from './Modal';

/**
 * The poster large on one side, the wording it was given on the other, so each line can be found
 * on the image and ticked off. Checking a poster against a 300px card thumbnail is what let five
 * invented names through, so this view exists to make the comparison readable.
 */
export default function ProofreadDialog({
  poster,
  name,
  open,
  checkedLines,
  onToggleLine,
  onConfirmAll,
  onClose,
}: {
  poster: Poster;
  /** "Top 5 v Grangemouth Railway" - which poster is being checked. */
  name: string;
  open: boolean;
  checkedLines: ReadonlySet<number>;
  onToggleLine: (index: number) => void;
  onConfirmAll: () => void;
  onClose: () => void;
}) {
  // Poster text is small when the whole poster is fitted to the window, which is exactly when a
  // misspelled name hides. Actual size makes the panel scroll instead of shrinking the image.
  const [actualSize, setActualSize] = useState(false);

  const total = poster.expectedText.length;
  const done = checkedLines.size;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="modal--proofread"
      title="Proofread before posting"
      subtitle={`${name} - ${done} of ${total} line${total === 1 ? '' : 's'} checked`}
      headActions={
        <button type="button" className="button button--ghost" onClick={() => setActualSize((v) => !v)}>
          {actualSize ? 'Fit to window' : 'Actual size'}
        </button>
      }
    >
      <div className="proofread-dialog__body">
        <div className={`proofread-dialog__image ${actualSize ? 'is-actual' : ''}`}>
          {poster.posterUrl ? (
            <img src={poster.posterUrl} alt={name} />
          ) : (
            <p className="muted">No image yet.</p>
          )}
        </div>

        <div className="proofread-dialog__lines">
          <p className="muted small">
            Tick each line once you have found it on the poster and checked its spelling. Then check
            the poster carries <strong>nothing else</strong> - any extra name, date or line was
            invented by the model and the poster needs regenerating.
          </p>

          <ul className="proofread-dialog__list">
            {poster.expectedText.map((line, index) => (
              <li key={`${line}-${index}`}>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={checkedLines.has(index)}
                    onChange={() => onToggleLine(index)}
                  />
                  <span>{line}</span>
                </label>
              </li>
            ))}
          </ul>

          <div className="proofread-dialog__foot">
            <button type="button" className="button button--primary" onClick={onConfirmAll}>
              {done === total ? 'Done' : 'Everything matches'}
            </button>
            {poster.posterUrl && (
              <a className="button button--ghost" href={poster.posterUrl} target="_blank" rel="noreferrer">
                Open image
              </a>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
